import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Avatar, Text, Flex, HStack, VStack, Icon, Grid, GridItem } from '@chakra-ui/react'
import { createEventApi } from '../../../utils/api-interface'
import { log } from '../../../utils/logger'
import { ParticipantStatus, ParticipantSummaryList } from '../../../services/network'
import { FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa'
import axios from 'axios'
import { RetryDialog } from '../../../components/RetryDialog/RetryDialog'

function EventPartecipant(): JSX.Element {
  const { t } = useTranslation()
  const { eventid } = useParams()
  const [eventPartecipants, setEventPartecipant] = useState<ParticipantSummaryList>()
  const [errorData, setErrorData] = useState<any>(null)
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false)
  const handleCloseRetryDialog = (): void => {
    setIsRetryDialogOpen(false)
  }

  const generateStarRating = (averageRating: number): JSX.Element[] => {
    const stars = []

    for (let i = 1; i <= 5; i++) {
      if (averageRating >= i) {
        stars.push(<Icon as={FaStar} key={i} color='yellow.500' />)
      } else if (averageRating >= i - 0.5) {
        stars.push(<Icon as={FaStarHalfAlt} key={i} color='yellow.500' />)
      } else {
        stars.push(<Icon as={FaRegStar} key={i} color='yellow.500' />)
      }
    }

    return stars
  }

  const fetchEventPartecipant = async (): Promise<void> => {
    if (eventid) {
      try {
        const eventApi = createEventApi()
        const response = await eventApi.getEventUsers(eventid)
        const p = response.data.participants.sort((a, b) => {
          const aName = `${a.firstName} ${a.lastName}`.toLocaleLowerCase()
          const bName = `${b.firstName} ${b.lastName}`.toLocaleLowerCase()
          return aName.localeCompare(bName)
        })
        setEventPartecipant({
          participants: [...p.filter((pp) => pp.status === ParticipantStatus.Invited), ...p.filter((pp) => pp.status !== ParticipantStatus.Invited)]
        })
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response) {
            log.error('Error fetching event partecipants:', error.response.data)
            setErrorData(error.response.data)
          } else {
            log.error('No response from the server:', error.message)
          }
        } else {
          log.error('Unexpected error:', error)
        }
        setIsRetryDialogOpen(true)
      }
    }
  }

  const handleConfirmRetryDialog = (): void => {
    setIsRetryDialogOpen(false)
    fetchEventPartecipant()
  }

  useEffect(() => {
    fetchEventPartecipant()
  }, [])

  useEffect(() => {
    log.debug(eventPartecipants)
  }, [eventPartecipants])

  const navigate = useNavigate()

  return (
    <Flex direction={'column'}>
      <Grid templateColumns='repeat(2, 1fr)' marginTop={'40px'} marginRight={'80px'} rowGap={'0px'} columnGap={'20px'}>
        {eventPartecipants?.participants && eventPartecipants?.participants.length > 0 ? (
          eventPartecipants.participants.map((participant, index: number) => (
            <GridItem
              height={'80px'}
              key={index}
              paddingTop={'8px'}
              className='clickableText'
              borderBottomColor={'GreyText'}
              borderBottomWidth={'1px'}
              justifyContent={'center'}
              onClick={(): void => navigate(`/users/userinfo/${participant.id}`)}>
              <HStack width={'inherit'} justifyContent={'space-between'}>
                <HStack>
                  <Avatar height={'56px'} width={'56px'} name='avatar' src={participant.profile?.profileImageUrl}></Avatar>
                  <VStack gap={'0px'} alignItems={'flex-start'} rowGap={1}>
                    <Text fontSize='16px' fontWeight='600' lineHeight='22.4px' textTransform={'capitalize'}>
                      {participant.firstName} {participant.lastName}
                    </Text>
                    <Text fontSize='14px' fontWeight='400' lineHeight='19.6px'>
                      {participant.role ? participant.role.it : ''}
                    </Text>
                    <HStack>{participant.experienceLevel ? generateStarRating(participant.experienceLevel) : <></>}</HStack>
                  </VStack>
                </HStack>
                <Flex direction={'column'}>
                  <Text variant={participant.status}>{t(`participant.${participant.status}`)}</Text>
                </Flex>
              </HStack>
            </GridItem>
          ))
        ) : (
          <Text marginLeft={'2px'} fontWeight='600' fontSize='16px' lineHeight='22.4px' color={'greyCold300'}>
            {t('no_participant_found')}
          </Text>
        )}
      </Grid>
      <RetryDialog isOpen={isRetryDialogOpen} onClose={handleCloseRetryDialog} onConfirm={handleConfirmRetryDialog} message={errorData} />
    </Flex>
  )
}

export { EventPartecipant }
